import React, { useState, useEffect } from 'react';
import { Users, Plus, Pencil, Loader2, UserCheck, UserX } from 'lucide-react';
import api from '../services/api';
import toast from 'react-hot-toast';
import { formatarDataHora } from '../utils/formatters';
import Modal from '../components/common/Modal';
import ConfirmDialog from '../components/common/ConfirmDialog';

const PERFIS = ['admin', 'contador', 'cliente'];

const BADGE_PERFIL = {
  admin:    'bg-violet-900/40 text-violet-400 border border-violet-800/30',
  contador: 'bg-blue-900/40 text-blue-400 border border-blue-800/30',
  cliente:  'bg-slate-700/60 text-slate-400 border border-slate-600/30',
};

const CAMPOS_VAZIOS = { nome: '', email: '', senha: '', perfil: 'cliente' };

export default function UsuariosPage() {
  const [usuarios,      setUsuarios]      = useState([]);
  const [loading,       setLoading]       = useState(true);
  const [modalAberto,   setModalAberto]   = useState(false);
  const [editando,      setEditando]      = useState(null);
  const [form,          setForm]          = useState(CAMPOS_VAZIOS);
  const [salvando,      setSalvando]      = useState(false);
  const [confirmToggle, setConfirmToggle] = useState(null); // { id, nome, ativo }

  useEffect(() => { carregar(); }, []);

  async function carregar() {
    setLoading(true);
    try {
      const { data } = await api.get('/usuarios');
      setUsuarios(data);
    } catch {
      toast.error('Erro ao carregar usuários.');
    } finally {
      setLoading(false);
    }
  }

  function abrirNovo() {
    setEditando(null);
    setForm(CAMPOS_VAZIOS);
    setModalAberto(true);
  }

  function abrirEditar(u) {
    setEditando(u);
    setForm({ nome: u.nome, email: u.email, senha: '', perfil: u.perfil });
    setModalAberto(true);
  }

  async function salvar() {
    if (!form.nome || !form.email) return toast.error('Nome e e-mail são obrigatórios.');
    if (!editando && form.senha.length < 8) return toast.error('Senha mínima: 8 caracteres.');

    setSalvando(true);
    try {
      if (editando) {
        const payload = { nome: form.nome, email: form.email, perfil: form.perfil, ativo: editando.ativo };
        const { data } = await api.put(`/usuarios/${editando.id}`, payload);
        setUsuarios(us => us.map(u => u.id === data.id ? { ...u, ...data } : u));
        toast.success('Usuário atualizado.');
      } else {
        const { data } = await api.post('/usuarios', form);
        setUsuarios(us => [...us, data]);
        toast.success('Usuário criado com sucesso.');
      }
      setModalAberto(false);
    } catch (err) {
      toast.error(err.response?.data?.erro || 'Erro ao salvar usuário.');
    } finally {
      setSalvando(false);
    }
  }

  async function toggleAtivo() {
    if (!confirmToggle) return;
    try {
      const novoAtivo = !confirmToggle.ativo;
      await api.put(`/usuarios/${confirmToggle.id}`, {
        nome: confirmToggle.nome, email: confirmToggle.email,
        perfil: confirmToggle.perfil, ativo: novoAtivo
      });
      setUsuarios(us => us.map(u => u.id === confirmToggle.id ? { ...u, ativo: novoAtivo } : u));
      toast.success(novoAtivo ? 'Usuário reativado.' : 'Usuário desativado.');
    } catch {
      toast.error('Erro ao alterar situação.');
    } finally {
      setConfirmToggle(null);
    }
  }

  if (loading) return (
    <div className="flex justify-center py-20">
      <Loader2 size={28} className="animate-spin text-primary-500" />
    </div>
  );

  return (
    <div className="space-y-4">
      {/* Cabeçalho com contagem e botão */}
      <div className="flex items-center justify-between">
        <p className="text-sm text-slate-500">{usuarios.length} usuário(s) cadastrado(s)</p>
        <button onClick={abrirNovo} className="btn-primary text-sm py-2">
          <Plus size={15} /> Novo Usuário
        </button>
      </div>

      {/* Tabela */}
      <div className="card p-0 overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-surface-border bg-surface/40">
              {['Usuário', 'Perfil', 'Situação', 'Último acesso', ''].map(h => (
                <th key={h} className="px-4 py-3 text-left text-xs font-semibold text-slate-500 uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody className="divide-y divide-surface-border">
            {usuarios.map(u => (
              <tr key={u.id} className="hover:bg-surface-card/50 transition-colors">
                <td className="px-4 py-3">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-gradient-to-br from-primary-600 to-primary-800 flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
                      {u.nome.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-slate-200 font-medium text-sm">{u.nome}</p>
                      <p className="text-slate-500 text-xs">{u.email}</p>
                    </div>
                  </div>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${BADGE_PERFIL[u.perfil]}`}>
                    {u.perfil}
                  </span>
                </td>
                <td className="px-4 py-3">
                  <span className={`text-xs px-2 py-0.5 rounded-full ${u.ativo ? 'bg-emerald-900/40 text-emerald-400' : 'bg-red-900/40 text-red-400'}`}>
                    {u.ativo ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td className="px-4 py-3 text-slate-500 text-xs">{formatarDataHora(u.ultimo_acesso) || 'Nunca'}</td>
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center gap-1 justify-end">
                    <button onClick={() => abrirEditar(u)} title="Editar"
                      className="p-1.5 rounded-lg text-slate-500 hover:text-primary-400 hover:bg-primary-900/20 transition-colors">
                      <Pencil size={14} />
                    </button>
                    <button onClick={() => setConfirmToggle(u)} title={u.ativo ? 'Desativar' : 'Reativar'}
                      className={`p-1.5 rounded-lg transition-colors ${u.ativo
                        ? 'text-slate-500 hover:text-red-400 hover:bg-red-900/20'
                        : 'text-slate-500 hover:text-emerald-400 hover:bg-emerald-900/20'}`}>
                      {u.ativo ? <UserX size={14} /> : <UserCheck size={14} />}
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal Criar/Editar */}
      <Modal
        aberto={modalAberto}
        onFechar={() => setModalAberto(false)}
        titulo={editando ? 'Editar Usuário' : 'Novo Usuário'}
      >
        <div className="space-y-4">
          <div>
            <label className="label">Nome completo</label>
            <input className="input" value={form.nome} onChange={e => setForm(f => ({...f, nome: e.target.value}))} placeholder="João da Silva" />
          </div>
          <div>
            <label className="label">E-mail</label>
            <input className="input" type="email" value={form.email} onChange={e => setForm(f => ({...f, email: e.target.value}))} placeholder="joao@email.com.br" />
          </div>
          {!editando && (
            <div>
              <label className="label">Senha (mínimo 8 caracteres)</label>
              <input className="input" type="password" value={form.senha} onChange={e => setForm(f => ({...f, senha: e.target.value}))} placeholder="••••••••" />
            </div>
          )}
          <div>
            <label className="label">Perfil de acesso</label>
            <select className="input" value={form.perfil} onChange={e => setForm(f => ({...f, perfil: e.target.value}))}>
              {PERFIS.map(p => <option key={p} value={p} className="capitalize">{p}</option>)}
            </select>
          </div>
          <div className="flex gap-2 justify-end pt-2">
            <button onClick={() => setModalAberto(false)} className="btn-secondary text-sm py-2 px-4">Cancelar</button>
            <button onClick={salvar} disabled={salvando} className="btn-primary text-sm py-2 px-4">
              {salvando ? <><Loader2 size={14} className="animate-spin" /> Salvando...</> : 'Salvar'}
            </button>
          </div>
        </div>
      </Modal>

      {/* Confirm toggle */}
      <ConfirmDialog
        aberto={!!confirmToggle}
        onFechar={() => setConfirmToggle(null)}
        onConfirmar={toggleAtivo}
        titulo={confirmToggle?.ativo ? 'Desativar usuário' : 'Reativar usuário'}
        mensagem={confirmToggle?.ativo
          ? `Deseja desativar o usuário "${confirmToggle?.nome}"? Ele não conseguirá mais fazer login.`
          : `Deseja reativar o usuário "${confirmToggle?.nome}"?`}
        textoBotao={confirmToggle?.ativo ? 'Desativar' : 'Reativar'}
        tipo={confirmToggle?.ativo ? 'danger' : 'warning'}
      />
    </div>
  );
}
